import { Color, Flex, Icon, Spacing, Text } from '@billy/design-system'
import { ReactNode, useCallback } from 'react'

import { useExpandedRows } from '../../contexts/expandedRowsContext'
import { BaseReportRow } from '../../types/baseTableTypes'
import { ReportRowType } from '../../types/reportRowType'
import * as Styled from './styles'
import { TruncatedText } from './TruncatedText'

interface TableRowProps<T extends BaseReportRow> {
  children: ReactNode
  row: T
}

export const TableRow = <T extends BaseReportRow>({ row, children }: TableRowProps<T>) => {
  const { isRowExpanded, toggleRow } = useExpandedRows()
  const { id, hasChildren, label, type, sublabel } = row

  const handleRowClick = useCallback(() => {
    if (type !== ReportRowType.Group || !hasChildren) {
      return
    }

    toggleRow(id)
  }, [type, hasChildren, toggleRow, id])

  const getLabelCell = () => {
    switch (type) {
      case ReportRowType.Header:
        return (
          <Styled.GroupHeaderCell>
            {sublabel && (
              <Text variant="bodySM" colorVariant="secondary" uppercase>
                {sublabel}
              </Text>
            )}
            <Text variant="h4">{label}</Text>
          </Styled.GroupHeaderCell>
        )
      case ReportRowType.Footer:
        return (
          <Styled.FooterCell>
            <Text variant="h4">{label}</Text>
          </Styled.FooterCell>
        )
      case ReportRowType.Group: {
        const isExpanded = isRowExpanded(id)
        return (
          <Styled.ExpandableHeader>
            <Flex alignItems="center" gap={Spacing.S8}>
              <Icon icon={isExpanded ? 'chevronUp' : 'chevronDown'} color={hasChildren ? Color.Black : Color.Grey200} />
              <Styled.TruncatedTextWrapper>
                <TruncatedText text={label} />
              </Styled.TruncatedTextWrapper>
            </Flex>
          </Styled.ExpandableHeader>
        )
      }
      default:
        return (
          <Styled.TableCell>
            <TruncatedText text={label} />
          </Styled.TableCell>
        )
    }
  }

  return (
    <Styled.TableRow
      key={id}
      clickable={type === ReportRowType.Group && hasChildren}
      type={type}
      nested={type === ReportRowType.Account}
      expanded={isRowExpanded(id) && hasChildren}
      onClick={handleRowClick}
    >
      {getLabelCell()}
      {children}
    </Styled.TableRow>
  )
}
